import { useState } from "react";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Props {
  variant?: "dark" | "light";
  compact?: boolean;
}

export function NewsletterSubscribe({ variant = "dark", compact = false }: Props) {
  const isLight = variant === "light";
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setError(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke("newsletter-subscribe", {
        body: { email: email.trim(), source: "footer" },
      });
      if (invokeError) throw invokeError;
      if ((data as { success?: boolean; error?: string })?.success) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
        setError((data as { error?: string })?.error || "Something went wrong.");
      }
    } catch (err: unknown) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Network error. Try again.");
    }
  };

  const form = (
    <>
      {status === "success" ? (
        <div
          className={cn(
            "flex items-center gap-2 rounded-lg px-3.5 py-2.5 text-sm",
            isLight ? "bg-primary/10 text-gray-900" : "bg-primary/15 text-foreground",
          )}
        >
          <Check className="h-4 w-4 text-primary shrink-0" />
          <span>You're in. Welcome aboard.</span>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <div
            className={cn(
              "group relative flex items-center rounded-lg transition-colors",
              "focus-within:ring-2 focus-within:ring-primary/40",
              isLight
                ? "bg-white border border-gray-200 focus-within:border-primary"
                : "bg-white/[0.04] border border-white/10 focus-within:border-primary/60",
            )}
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              maxLength={255}
              aria-label="Email address"
              className={cn(
                "flex-1 h-11 bg-transparent px-3.5 text-sm outline-none",
                isLight
                  ? "text-gray-900 placeholder:text-gray-400"
                  : "text-foreground placeholder:text-white/35",
              )}
            />
            <button
              type="submit"
              disabled={status === "loading"}
              aria-label="Subscribe"
              className={cn(
                "mr-1 my-1 inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-3.5",
                "bg-primary text-primary-foreground text-sm font-medium transition-all",
                "hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20",
                "active:scale-[0.98] disabled:opacity-60",
              )}
            >
              {status === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Subscribe
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </div>

          {status === "error" && error && (
            <p className="mt-2 text-xs text-destructive">{error}</p>
          )}
        </form>
      )}
    </>
  );

  if (compact) return form;

  // Standalone variant (with its own headline) — kept for non-footer use
  return (
    <div>
      <h2 className={cn(
        "text-sm font-semibold mb-3",
        isLight ? "text-gray-900" : "text-foreground",
      )}>
        Get the playbook
      </h2>
      <p className={cn(
        "text-sm leading-relaxed mb-4",
        isLight ? "text-gray-600" : "text-white/60",
      )}>
        Monthly bid &amp; tender tactics. No spam.
      </p>
      {form}
    </div>
  );
}
